import { ODSLoadingSpinner } from '@telekom-ods/react-ui-kit';
import type { FormikValues } from 'formik';
import { useState, useEffect, type FC } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../../api/auth';
import Provision from '../../../../api/provision';
import { FormModal } from '../../../../components/Formik';
import { regexValidator } from '../../../../helpers/regexValidator';
import useApiQuery from '../../../../hooks/useApiQuery';
import useJuiceGroupValidation from '../../../../hooks/useJuiceGroupValidation';

import type { APIHookArg } from '@/types';
import type { FieldConfig, FormikFormField } from '@/types/formTypes';
import type { Organization } from '@/types/organization';
import type { Project } from '@/types/projectTypes';

interface CreateProductFormModalProps {
  onSubmit: (data: unknown) => void;
  onCancel: () => void;
}

const CreateProductFormModal: FC<CreateProductFormModalProps> = ({ onSubmit, onCancel }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isCheckingJuiceGroup, juiceGroupError, checkJuiceGroup } = useJuiceGroupValidation();

  const [project, setProject] = useState<Project | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);

  const { error: fetchError, makeApiCall: getProjectCall } = useApiQuery<Project>(
    Auth.fetchV2ProjectById as unknown as (...args: unknown[]) => Promise<Project>
  );
  const { makeApiCall: getOrganizationCall } = useApiQuery(
    Auth.fetchV1OrganizationById as unknown as APIHookArg
  );
  const { makeApiCall: createProductCall } = useApiQuery(
    Auth.createV2Product as unknown as APIHookArg,
    {
      successMessage: 'productCreation',
    }
  );
  const { data: juiceTags, makeApiCall: getTagsCall } = useApiQuery<string[]>(Provision.fetchTags);

  useEffect(() => {
    if (!projectId) return;

    const getTags = async (): Promise<void> => {
      await getTagsCall();
    };

    const getProjectAndOrganization = async (): Promise<void> => {
      const projData = await getProjectCall(projectId);
      if (!projData) return;
      setProject(projData);

      const orgData = await getOrganizationCall(projData.organizationId);
      if (orgData && typeof orgData === 'object' && '_id' in orgData) {
        setOrganization(orgData as Organization);
      }
    };

    getTags();
    getProjectAndOrganization();
  }, [getOrganizationCall, getProjectCall, getTagsCall, projectId]);

  const productTypes =
    organization?.marketplaceItems?.reduce<Record<string, string>>((acc, curr) => {
      if (!['Safescarf', 'Evaluation', 'Monitoring'].includes(curr.name)) {
        acc[curr.name] = curr.name;
      }
      return acc;
    }, {}) ?? {};

  const handleJuiceGroupChange = (
    value: string,
    { setFieldValue }: { setFieldValue: (field: string, value: string) => void }
  ): void => {
    setFieldValue('juiceGroup', value);
    checkJuiceGroup(value);
  };

  const fields: FieldConfig[] = [
    {
      fieldType: 'text',
      name: 'name',
      label: 'Product Name',
      onChange: (e, { setFieldValue }) => {
        const updatedJuiceGroup = `${project?.organization?.enterpriseName ?? ''}-${project?.name ?? ''}-${e.target.value}`;
        handleJuiceGroupChange(updatedJuiceGroup, { setFieldValue });
      },
    },
    {
      fieldType: 'text',
      name: 'juiceGroup',
      label: 'Juice Group',
      InputProps: {
        endAdornment: isCheckingJuiceGroup ? (
          <div>
            <ODSLoadingSpinner
              labelText='Validating Juice Group.... '
              style={{
                marginTop: '0.5rem',
              }}
              size='small'
            />
          </div>
        ) : null,
      },
      onChange: (e, { setFieldValue }) => {
        handleJuiceGroupChange(e.target.value, { setFieldValue });
      },
      helperText: juiceGroupError,
    },
    {
      fieldType: 'text',
      name: 'description',
      label: 'Description',
    },
    {
      fieldType: 'select',
      name: 'type',
      label: 'Product Type',
      menuItems: Object.keys(productTypes).map(key => ({
        value: key,
        label: productTypes[key] ?? '',
      })),
    },
    {
      fieldType: 'tags',
      name: 'tags',
      label: 'Juice Group Tags',
      tags: juiceTags,
    },
  ];

  const initialValues: FormikValues = {
    name: '',
    juiceGroup: `${project?.organization?.enterpriseName ?? ''}-${project?.name ?? ''}-`,
    description: '',
    type: '',
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Required')
      .matches(regexValidator.name, 'All lower-case, no spaces, no special characters (dash only)'),
    juiceGroup: Yup.string()
      .required('Required')
      .matches(
        regexValidator.juiceGroup,
        'The Juice Group must start with a letter and contain only lower-case letters, numbers, and dashes'
      )
      .min(3, 'The Juice Group must be at least 3 characters long')
      .max(64, 'The Juice Group must be at most 64 characters long'),
    description: Yup.string().required('Required'),
    type: Yup.string().required('Required'),
  });

  const createProduct = async (formValues: FormikValues): Promise<void> => {
    onCancel();

    const data = await createProductCall({
      ...formValues,
      projectId,
      organizationId: project?.organizationId,
    });

    if (data) {
      onSubmit(data);
    }
  };

  return (
    organization && (
      <FormModal
        title='Create New Product'
        fields={fields as FormikFormField[]}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={formValues => {
          void createProduct(formValues);
        }}
        onCancel={onCancel}
        submitLabel='Create'
        error={fetchError}
      />
    )
  );
};

export default CreateProductFormModal;
